import React, { useState, useEffect } from "react";
import config from '../config/config';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Grid,
  Alert,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from "@mui/material";

const PaymentModal = ({ open, onClose, invoice, invoiceItems, onSubmit }) => {
  const [transactionCode, setTransactionCode] = useState("");
  const [paymentInputs, setPaymentInputs] = useState({});
  const [paymentMode, setPaymentMode] = useState("");
  const [paymentNote, setPaymentNote] = useState("");
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);

  // Reset state on modal open & set inputs to balance (total - paid_amount)
  useEffect(() => {
    if (open && invoiceItems?.length) {
      const initialInputs = {};
      invoiceItems.forEach((item) => {
        const balance = parseFloat(item.total) - parseFloat(item.paid || 0);
        initialInputs[item.id] = balance >= 0 ? balance.toFixed(2) : "0.00";
      });
      setPaymentInputs(initialInputs);
      setTransactionCode("");
      setPaymentMode("");
      setPaymentNote("");
      setErrors({});
      setMessage(null);
      setLoading(false);
    }
  }, [open, invoiceItems]);

  // Validate inputs before submit
  const validateInputs = () => {
    const newErrors = {};
    let hasError = false;

    if (!transactionCode.trim()) {
      newErrors.transactionCode = "Transaction code is required";
      hasError = true;
    }

    if (!paymentMode) {
      newErrors.paymentMode = "Please select a payment mode";
      hasError = true;
    }

    invoiceItems.forEach((item) => {
      const val = paymentInputs[item.id];
      const balance = parseFloat(item.total) - parseFloat(item.paid || 0);

      if (val === "" || val === undefined) {
        newErrors[item.id] = "Amount is required";
        hasError = true;
      } else if (isNaN(val)) {
        newErrors[item.id] = "Amount must be a number";
        hasError = true;
      } else if (parseFloat(val) < 0) {
        newErrors[item.id] = "Amount cannot be negative";
        hasError = true;
      } else if (parseFloat(val) > balance) {
        newErrors[item.id] = `Amount cannot exceed balance (KES ${balance.toFixed(2)})`;
        hasError = true;
      }
    });

    setErrors(newErrors);
    return !hasError;
  };

  const handleInputChange = (itemId, maxAmount, value) => {
    // Allow empty string for clearing input
    if (value === "") {
      setPaymentInputs((prev) => ({ ...prev, [itemId]: "" }));
      setErrors((prev) => ({ ...prev, [itemId]: null }));
      return;
    }

    // Only allow numbers with optional decimals
    // Reject if value contains non-numeric characters (except dot)
    if (!/^\d*\.?\d*$/.test(value)) {
      setErrors((prev) => ({ ...prev, [itemId]: "Invalid amount" }));
      return;
    }

    const amount = parseFloat(value);

    if (isNaN(amount) || amount < 0) {
      setErrors((prev) => ({ ...prev, [itemId]: "Invalid amount" }));
      return;
    }

    if (amount > maxAmount) {
      setErrors((prev) => ({
        ...prev,
        [itemId]: `Amount cannot exceed balance (KES ${maxAmount.toFixed(2)})`,
      }));
      return;
    }

    setPaymentInputs((prev) => ({ ...prev, [itemId]: value }));
    setErrors((prev) => ({ ...prev, [itemId]: null }));
  };

  const handleSave = async () => {
    setMessage(null);
    if (!validateInputs()) {
      setMessage({ type: "error", text: "Please fix validation errors." });
      return;
    }

    const payload = {
      invoice_id: invoice,
      transaction_code: transactionCode,
      payment_mode: paymentMode,
      payment_note: paymentNote,
      payments: Object.entries(paymentInputs).map(([itemId, amount]) => ({
        item_id: itemId,
        amount_paid: parseFloat(amount),
      })),
    };

    try {
      setLoading(true);
      const response = await onSubmit(payload);
      if (response.success) {
        setMessage({ type: "success", text: response.message || "Payment saved successfully." });
      } else {
        setMessage({ type: "error", text: response.message || "Failed to save payment." });
      }
    } catch (error) {
      setMessage({ type: "error", text: error.message || "An error occurred." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        Add Payment For Invoice #{invoice?.invoiceNumber || invoice?.invoice_number || invoice}
      </DialogTitle>

      <DialogContent dividers>
        {message && (
          <Alert severity={message.type} sx={{ mb: 2 }}>
            {message.text}
          </Alert>
        )}

        <FormControl fullWidth sx={{ mb: 3 }} error={Boolean(errors.transactionCode)}>
          <TextField
            label="Transaction Code"
            value={transactionCode}
            onChange={(e) => setTransactionCode(e.target.value)}
            disabled={loading}
            error={Boolean(errors.transactionCode)}
            helperText={errors.transactionCode}
          />
        </FormControl>

        <FormControl fullWidth sx={{ mb: 3 }} error={Boolean(errors.paymentMode)}>
          <InputLabel id="payment-mode-label">Payment Mode</InputLabel>
          <Select
            labelId="payment-mode-label"
            value={paymentMode}
            label="Payment Mode"
            onChange={(e) => setPaymentMode(e.target.value)}
            disabled={loading}
          >
            <MenuItem value="Mpesa">Mpesa</MenuItem>
            <MenuItem value="Bank">Bank</MenuItem>
            <MenuItem value="Cash">Cash</MenuItem>
          </Select>
          {errors.paymentMode && (
            <Typography variant="caption" color="error">
              {errors.paymentMode}
            </Typography>
          )}
        </FormControl>

        {invoiceItems?.length ? (
          invoiceItems.map((item) => {
            const balance = parseFloat(item.total) - parseFloat(item.paid) || 0;
            return (
              <Grid container spacing={2} key={item.id} sx={{ mb: 2 }} alignItems="center">
                <Grid item xs={6}>
                  <Typography>{item.description}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    Balance: KES {balance.toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Amount Paid"
                    fullWidth
                    type="text"
                    inputProps={{ inputMode: "decimal", pattern: "[0-9]*[.,]?[0-9]*" }}
                    value={paymentInputs[item.id] ?? ""}
                    placeholder={balance.toFixed(2)}
                    onChange={(e) => handleInputChange(item.id, balance, e.target.value)}
                    error={Boolean(errors[item.id])}
                    helperText={errors[item.id]}
                    disabled={loading}
                  />
                </Grid>
              </Grid>
            );
          })
        ) : (
          <Typography>No invoice items found.</Typography>
        )}

        <TextField
          label="Payment Note"
          multiline
          rows={3}
          fullWidth
          value={paymentNote}
          onChange={(e) => setPaymentNote(e.target.value)}
          disabled={loading}
          sx={{ mb: 3 }}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSave} color="primary" variant="contained" disabled={loading}>
          {loading ? "Saving..." : "Save Payment"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentModal;
